import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  Title,
  Text,
  Button,
  Stack,
  Paper,
  Group,
  RingProgress,
  Box,
  Loader,
  Center,
} from '@mantine/core';
import { IconStar, IconTrophy, IconVocabulary } from '@tabler/icons-react';
import { useStory } from '@/features/story/hooks/useStory';
import { WrapUpModal } from '@/features/story/components/WrapUpModal';
import { usePlatform } from '@/hooks/usePlatform';
import { useQuery } from '@tanstack/react-query';
import { flashcardApi } from '@/features/flashcards/api/flashcardApi';

/**
 * Mobile Quiz Results Page
 *
 * Celebrates quiz completion with:
 * - Large score display
 * - Encouraging messages
 * - Clear next actions
 * - Mobile-optimized layout
 */
export function MobileResultsPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const { id: sessionId } = useParams<{ id: string }>();
  const { isIOS } = usePlatform();
  const result = location.state?.result;
  const [wrapUpOpened, setWrapUpOpened] = useState(false);

  // Get story data
  const storyId = result?.story_id || location.state?.storyId;
  const { data: story, isLoading: storyLoading } = useStory(storyId);

  // Check for auto-generated vocab deck
  const { data: vocabDeck } = useQuery({
    queryKey: ['vocab-deck', storyId],
    queryFn: () => flashcardApi.getStoryVocabDeck(storyId!),
    enabled: !!storyId,
    retry: false,
  });

  // Show wrap-up modal after delay
  useEffect(() => {
    if (result && !wrapUpOpened) {
      const timer = setTimeout(() => {
        setWrapUpOpened(true);
      }, 1500);
      return () => clearTimeout(timer);
    }
  }, [result, wrapUpOpened]);

  const handleWrapUpSuccess = () => {
    navigate('/library');
  };

  if (!result) {
    return (
      <Center h="100vh">
        <Text c="dimmed">No results found</Text>
      </Center>
    );
  }

  if (storyLoading) {
    return (
      <Center h="100vh">
        <Loader color="teal" size="lg" />
      </Center>
    );
  }

  const percentage = (result.score / result.total_questions) * 100;
  const isPerfect = result.score === result.total_questions;
  const isGoodScore = percentage >= 70;

  // Encouraging messages based on score
  const getMessage = () => {
    if (isPerfect) return "Perfect Score! Amazing!";
    if (percentage >= 90) return "Outstanding Work!";
    if (percentage >= 70) return "Great Job!";
    if (percentage >= 50) return "Good Effort!";
    return "Keep Practicing!";
  };

  return (
    <>
      <Box
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          display: 'flex',
          flexDirection: 'column',
          backgroundColor: '#f8f9fa',
          paddingTop: isIOS ? 'env(safe-area-inset-top)' : 0,
        }}
      >
        {/* Results Content */}
        <Box
          style={{
            flex: 1,
            overflow: 'auto',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '24px',
          }}
        >
          <Stack gap="xl" align="center" style={{ maxWidth: '400px', width: '100%' }}>
            {/* Score Circle */}
            <Paper p="xl" withBorder ta="center" w="100%">
              <Stack gap="xl" align="center">
                {isPerfect && (
                  <Box style={{ color: '#fcc419' }}>
                    <IconTrophy size={64} />
                  </Box>
                )}

                <RingProgress
                  size={220}
                  thickness={20}
                  sections={[
                    {
                      value: percentage,
                      color: isPerfect ? 'yellow' : isGoodScore ? 'teal' : 'orange'
                    }
                  ]}
                  label={
                    <Stack gap={4} align="center">
                      <Text size="48px" fw={700} style={{ lineHeight: 1 }}>
                        {result.score}
                      </Text>
                      <Text size="lg" c="dimmed">
                        out of {result.total_questions}
                      </Text>
                      <Text size="xl" fw={600} mt="xs" c={isGoodScore ? 'teal' : 'orange'}>
                        {percentage.toFixed(0)}%
                      </Text>
                    </Stack>
                  }
                />

                <Stack gap="xs" align="center">
                  <Title order={2} style={{ fontFamily: "'Comic Neue', 'Comic Sans MS', cursive" }}>
                    {getMessage()}
                  </Title>
                  <Text c="dimmed" ta="center">
                    {story?.title}
                  </Text>
                </Stack>

                {isPerfect && (
                  <Stack gap="xs" align="center">
                    <Group gap="xs">
                      {[1, 2, 3, 4, 5].map((i) => (
                        <IconStar key={i} size={24} fill="#fcc419" color="#fcc419" />
                      ))}
                    </Group>
                    <Text size="sm" c="dimmed" ta="center">
                      Perfect! You answered every question correctly!
                    </Text>
                  </Stack>
                )}
              </Stack>
            </Paper>

            {/* Reading Time */}
            {result.reading_time_seconds && (
              <Paper p="md" withBorder w="100%">
                <Stack gap="xs" align="center">
                  <Text size="sm" c="dimmed">
                    Reading Time
                  </Text>
                  <Text size="lg" fw={600}>
                    {Math.floor(result.reading_time_seconds / 60)}m {result.reading_time_seconds % 60}s
                  </Text>
                </Stack>
              </Paper>
            )}
          </Stack>
        </Box>

        {/* Bottom Actions */}
        <Box
          style={{
            backgroundColor: 'white',
            borderTop: '1px solid #e9ecef',
            paddingBottom: isIOS ? 'calc(16px + env(safe-area-inset-bottom))' : '16px',
          }}
        >
          <Stack p="md" gap="sm">
            <Button
              fullWidth
              size="lg"
              variant="filled"
              color="teal"
              onClick={() => navigate('/library')}
            >
              Back to Library
            </Button>
            {vocabDeck && (
              <Button
                fullWidth
                size="md"
                variant="light"
                color="grape"
                leftSection={<IconVocabulary size={18} />}
                onClick={() => navigate(`/flashcards/deck/${vocabDeck.id}`)}
              >
                Study Vocabulary ({vocabDeck.card_count} words)
              </Button>
            )}
            <Button
              fullWidth
              size="md"
              variant="subtle"
              onClick={() => navigate('/')}
            >
              Go Home
            </Button>
          </Stack>
        </Box>
      </Box>

      {/* Wrap-up Modal */}
      <WrapUpModal
        opened={wrapUpOpened}
        onClose={() => setWrapUpOpened(false)}
        sessionId={sessionId}
        storyId={storyId}
        onSuccess={handleWrapUpSuccess}
      />
    </>
  );
}
